import { useTranslation } from "react-i18next"
import PageTitle from "../components/PageTitle"
import styled from "styled-components"
import { zeroLineHeight, wrapWhenNarrow } from '../styles/mixins'
import { useEffect, useRef, useState } from "react"
import { child, get, getDatabase, ref, set, update } from "firebase/database"
import fbConf from "../FirebaseConf"
import WhiteSpace from "../components/Whitespace"
import { useOutletContext, useSearchParams } from "react-router-dom"
import Dropdown from "../components/Dropdown"
import Modal from "../components/Modal"
import Input from "../components/Input"
import Select from "../components/Select"
import InputWithIcon from "../components/InputWithIcon"
import LoaderDots from "../components/LoaderDots"
import Button from "../components/Button"
import ListItemWithDeleteButton from "../components/ListItemWithDeleteButton"
import React from "react"
import ListItem from "../components/ListItem"
import Comm from "../Comm"
import ButtonWithIcon from "../components/ButtonWithIcon"
import dateToTimeAgoString from "../utils/dateToTimeAgoString"
import Navigator from "../components/Navigator"
import Icon from "../Icon"
import { useConfirm } from "../ConfirmProvider"
import { useNotification } from "../modules/Notification"
import normalizeString from "../utils/normalizeString"
import InputWithLabel from "../components/InputWithLabel"
import { deduplicateFiles, displayNameHadPlatform, getMissingPlatforms, resolveForSave, stripPlatform } from "../utils/platformVersions"
import { BREAKPOINTS, TAP_MIN } from "../GlobalTheme"

interface ChargerType {
  chargerId: string,
  fwVersion: string,
  osVersion: string,
  swVersion: string,
  fwVersionNumber: string,
  osVersionNumber: string,
  swVersionNumber: string,
  userEmail: string,
  online: boolean,
  lastPresence: string,
  userId: string,
  localName: string,
  comments: { author: string, comment: string }[]
}

interface ChargerGroupType {
  chargers: {
    [key: string]: ChargerType
  },
  groupFwVersion: string,
  groupOsVersion: string,
  groupSwVersion: string,
  lastEdit?: string,
  frontendOrder?: number,
  frontendFilter?: { SW?: string, FW?: string, OS?: string }
}

interface ChargerGroupsType {
  [key: string]: ChargerGroupType
}

const GroupsContainer = styled.div`
  width: 100%;
  height: calc(90vh - 170px);
  @media (max-width: ${BREAKPOINTS.shell}px) {
    height: auto;
    max-height: calc(100dvh - 160px);
    overscroll-behavior: contain;
  }
  overflow-y: scroll;
`

const GroupContainer = styled.div`
  width: calc(100% - 30px - 16px - 39px);
  margin-left: 25px;
  margin-bottom: 8px;
  background-color: ${props => props.theme.colors.white};
  border-radius: 24px;
  padding-top: 10px;
  padding-left: 39px;
  /* The edit button sits at right:70px and the collapse chevron at right:24px, both absolute against
     this card. Padding cannot move them, but it does keep the card's CONTENT - the header row and
     the expanded charger table alike - from reaching them. */
  padding-right: 100px;
  /* Without this the width above is the CONTENT width, so the padding reserved for the buttons is
     ADDED to the card - pushing the card, and the buttons with it, past the viewport. */
  box-sizing: border-box;
  position: relative;

  /* 25px margin + 39px padding + an 85px width deduction leaves ~240px of content on a phone. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: auto;
    margin-left: 0px;
    padding-left: 16px;
    padding-right: 16px;
  }
`

/* The header row is a grid, which takes it out of the table layout algorithm - so the table and its
   body have to be blocks too, or the table keeps sizing itself from cell content and the row grows
   past the card (pushing the edit and collapse buttons out of view). */
const GroupHeaderTable = styled.table`
  display: block;
  width: 98%;
  cursor: pointer;

  > tbody {
    display: block;
  }
`

const GroupHeaderRow = styled.tr`
  /* Grid rather than flex: flex cells refuse to shrink past their min-content, so the row grew wider
     than the card and pushed the absolutely positioned edit and collapse buttons out of view. Grid
     tracks are exact - the row always fits and the cells truncate inside their share. */
  display: grid;
  /* auto-fit with a real minimum rather than fixed percentages: the percentage tracks fitted, but
     each one was mostly icon, so every value truncated to a character or two. */
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  align-items: start;
  gap: 0px 8px;

  > td {
    min-width: 0;
    /* The cells carry percentage widths meant for a flex row; inside a grid track a 10% cell becomes
       15px and every value truncates to a character. The track sets the width here. */
    width: auto;
  }

  /* Six percentage columns need ~1000px. On a phone they become a grid of label/value pairs, which
     keeps every pair together and every card on the same offsets. */
  @media (max-width: ${BREAKPOINTS.phone}px) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    align-items: start;
    gap: 10px 16px;
    padding-right: 0px;

    > td {
      width: auto !important;
      min-width: 0;
    }

    /* The name takes its own row and reserves the strip where the card's absolutely positioned
       edit and collapse buttons sit. */
    > td:first-child {
      grid-column: 1 / -1;
      padding-right: 84px;
    }
  }
`

const GroupNameLabel = styled.p`
  font-weight: 700;
  font-size: 18px;
  color: ${props => props.theme.colors.gray_900};
  line-height: 30px;
  margin-top: 10px;
  /* A fixed share of the name cell, not flex:1 1 auto - sizing it from its own content made every
     row shrink differently at 1280, so each following column started at a different x. Truncation
     (safe here thanks to the 30px line-height) replaces the old mid-word break. */
  width: max(10vw, 128px);
  flex: 0 0 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* In the stacked grid the cell sets the width; a fixed one overflows into the next cell. */
  @media (max-width: ${BREAKPOINTS.phone}px) {
    width: auto;
    flex: 1 1 auto;
  }
`

const GroupInfoContainer = styled.td<{ $width?: string }>`
  display: flex;
  min-width: 0;
  /* The header row is a grid, so the track sets the width; this stays for the expanded table's
     cells, which are still laid out as flex rows. */
  width: ${props => props.$width ? props.$width : 'auto'};

  /* The label/value pair sits in an intermediate div. Without this the div keeps its max-content
     width, so the cell shrinking does nothing and the value still overflows into the next column. */
  > div {
    min-width: 0;
  }
`

const GroupInfoLabel = styled.p`
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  ${zeroLineHeight}
  margin-top: 16px;
`

const GroupInfoValue = styled.p`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_900};
  ${zeroLineHeight}
  margin-top: 16px;
  ${wrapWhenNarrow}
  /* A version string is one unbreakable token in a narrow percentage cell. It must not wrap (the
     zero line-height above would paint line two on line one) and it must not overflow into the next
     column, so on desktop it truncates. Below the breakpoint the cells wrap onto their own rows and
     wrapWhenNarrow lets the token break instead. */
  @media (min-width: ${BREAKPOINTS.shell + 1}px) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    /* Clipping needs a real line box: with the inherited line-height: 0 the content box is zero
       tall, so overflow: hidden clips the text away completely rather than truncating it. The top
       margin is reduced by half the line height so the glyphs stay where they were. */
    line-height: 1.2;
    margin-top: 9px;
  }
`

const EditButtonContainer = styled.div`
  width: 16px;
  height: 16px;
  position: absolute;
  top: 28px;
  right: 70px;
  cursor: pointer;

  /* A 16px icon is not a touch target. Grown to 44px and re-anchored so the icon stays put. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: ${TAP_MIN}px;
    height: ${TAP_MIN}px;
    top: 14px;
    right: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:active {
    transform: scale(.9);
  }

`

const OpenButtonContainer = styled.div<{ $isOpen: boolean }>`
  width: 16px;
  height: 16px;
  position: absolute;
  padding: 7px;
  background-color: #EFF2F5;
  border-radius: 6px;
  top: 22px;
  right: 24px;

  /* Same: a 30px chevron becomes a 44px target, re-anchored to keep the glyph in place. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: ${TAP_MIN}px;
    height: ${TAP_MIN}px;
    box-sizing: border-box;
    padding: 14px;
    top: 15px;
    right: 17px;
  }

  transform: rotate(${props => props.$isOpen ? '0deg' : '180deg'});
  cursor: pointer;

  &:active {
    transform: rotate(${props => props.$isOpen ? '0deg' : '180deg'}) scale(.9);
  }

`

const GroupDividerLine = styled.div`
  width: 98%;
`

/* Keeps the wide expanded charger table inside its card on a phone, instead of the whole page
   panning. */
const GroupChargersScroller = styled.div`
  @media (max-width: ${BREAKPOINTS.shell}px) {
    overflow-x: auto;
    overscroll-behavior-x: contain;
  }
`

const GroupChargersTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: max-content;
    min-width: 100%;
  }
`

const GroupChargersTableLabel = styled.p`
  font-weight: 600;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_500};
`

const ChargerIdLabel = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  line-height: 16px;
  margin: 0px;
  margin-top: 4px;
`

const ChargerEmailLabel = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_500};
  line-height: 16px;
  margin: 0px;
  margin-top: 2px;
`

const ChargerLocalNameLabel = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_500};
  line-height: 16px;
  margin: 0px;
  margin-top: 2px;
  margin-bottom: 4px;
`

const ChargerSoftwareVerisonLabel = styled.p<{ $isMismatch?: boolean }>`
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.$isMismatch ? props.theme.colors.danger : props.theme.colors.gray_500};
  top: 4px;
  position: absolute;
`

const ChargerTableRow = styled.tr`
  border-bottom: 1px dashed ${props => props.theme.colors.gray_300};
  position: relative;
`

const ChargerInfoRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.gray_300};
`

const ChargerStatus = styled.p<{ $status: string }>` // online / offline / disabled / charging
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.theme.colors.white};
  border-radius: 40px;
  width: 70px;
  line-height: 22px;
  text-align: center;
  background-color: ${props => {
    switch (props.$status) {
      case 'online':
        return props.theme.colors.success
      case 'offline':
        return props.theme.colors.gray_400
      case 'disabled':
        return props.theme.colors.gray_900
      case 'charging':
        return props.theme.colors.primary
    }
  }};
`

const ModalCloseButtonContainer = styled.div`
  position: absolute;
  top: 26px;
  right: 26px;
`

const ModalContainer = styled.div`
  padding: 24px;
  height: 85dvh;
  overflow-y: scroll;
`

const ModalTitle = styled.p`
  font-weight: 600;
  font-size: 20px;
  margin: 0px;
  color: ${props => props.theme.colors.gray_900};
`

const ModalCategoryTitle = styled.p`
  font-weight: 700;
  font-size: 14px;
  margin: 0px;
  color: ${props => props.theme.colors.gray_900};
`

const ModalCategoryDivider = styled.div`
  width: min(max(calc(18vw + 24px), 254.4px), calc(100vw - 72px));
  height: 4px;
  border-radius: 4px;
  background-image: linear-gradient(to right, #2A6FF4, #E2594B);
`

const ModalChargerContainer = styled.div`
  width: calc(100% - 12px);
  background-color: ${props => props.theme.colors.white};
  padding: 6px;
  border-radius: 12px;
  display: flex;
  margin-top: 16px;
  cursor: pointer;
`

const ModalChargerLabel = styled.p`
  font-weight: 400;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  ${zeroLineHeight}
  margin-top: 10px;
  margin-left: 12px;
`

const ModalChargerValue = styled.p`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_900};
  ${zeroLineHeight}
  margin-top: 18px;
  margin-left: 12px;
`

const ModalSaveButtonContainer = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0px;
  width: calc(100% - 10px);
  background-color: ${props => props.theme.colors.background};
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
`

const ModalSaveButton = styled.p`
  font-weight: 600;
  font-size: 15px;
  color: ${props => props.theme.colors.white};
  padding-top: 8px;
  padding-bottom: 8px;
  margin: 16px;
  width: 20%;
  text-align: center;
  border-radius: 12px;
  float: right;
  background-color: ${props => props.theme.colors.dark};
  cursor: pointer;
`

const ModalCancelButton = styled.p`
  font-weight: 600;
  font-size: 15px;
  color: ${props => props.theme.colors.gray_500};
  padding-top: 8px;
  padding-bottom: 8px;
  width: 20%;
  text-align: center;
  float: right;
  cursor: pointer;
`

const ToolbarButtonContainer = styled.div`
  position: absolute;
  top: 72px;
  right: 26px;
  display: flex;

  /* Absolutely positioned toolbars cannot wrap, so on a phone they overlay the page title and
     their leading buttons become unreachable. Put the row back in flow and let it wrap. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    top: auto;
    right: auto;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0px 0px 12px 25px;
  }
`

const SmallModalContainer = styled.div`
  padding: 24px;
`

const SmallModalLabel = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
`

const SmallModalButtonContainer = styled.div`
  width: max(15vw, 192px);
  display: flex;
`

const BigModalContainer = styled.div`
  padding-left: 88px;
  padding-right: 88px;
  padding-top: 65px;
  padding-bottom: 65px;
`

const UploadFilesTitle = styled.p`
  font-weight: 600;
  font-size: 20px;
  color: ${props => props.theme.colors.gray_900};
  width: max(22vw, 281.6px);
  text-align: center;
  margin: 0px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: 100%;
  }
`

const UploadFilesSubTitle = styled.p`
  font-weight: 600;
  font-size: 16px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
`

const FileDropContainer = styled.div<{ $isDraggingOver?: boolean }>`
  width: 100%;
  border-radius: 16px;
  background-color: ${props => props.$isDraggingOver ? props.theme.colors.primary + '22' : props.theme.colors.primaryLight};
  border: ${props => props.$isDraggingOver ? `2px solid ${props.theme.colors.primary}` : `1px dashed ${props.theme.colors.primary}`};
  cursor: pointer;
  transition: background-color 0.15s, border 0.15s;
`

const FileDropLabel = styled.p`
  font-weight: 600;
  font-size: 16px;
  color: ${props => props.theme.colors.gray_900};
  width: 100%;
  text-align: center;
  margin: 0px;
`

const DragDropFilesErrorLabel = styled.p`
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.theme.colors.danger};
  width: 100%;
  text-align: center;
`

const ChargerSoftwareIconContainer = styled.div`
  width: 12px;
  height: 12px;
  margin-top: -4px;
  cursor: pointer;
`

const TableTimeAgoLabel = styled.p`
  font-weight: 400;
  font-size: 11px;
  color: ${props => props.theme.colors.gray_500};
  ${zeroLineHeight}
  margin-top: -4px;
  width: 100px;
  margin-left: -15px;
  text-align: center;
`

const FilterDropdownContainer = styled.div<{ $isActive: boolean }>`
  background-color: ${props => props.$isActive ? '#673BE5' : props.theme.colors.dark};
  height: 40px;
  padding-right: 5px;
  padding-left: 5px;
  border-radius: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
`

const SearchHelpBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  cursor: pointer;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #E4E6EF;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Keep the 18px circle; grow only the tappable area (::after belongs to this element). */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: ${TAP_MIN}px;
      height: ${TAP_MIN}px;
      border-radius: 50%;
    }
  }
`

const RefreshTimerLabel = styled.p`
  font-weight: 400;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_600};
  margin-top: 4px;
  text-align: center;
`

const ReleaseNotesContainer = styled.div`
  width: calc(100% - 84px);
  border-radius: 16px;
  background-image: linear-gradient(to right, #313860 , #151928);
  display: flex;
  padding: 16px;
  padding-left: 24px;
  margin-left: 19px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: auto;
    margin-left: 0px;
    box-sizing: border-box;
    flex-wrap: wrap;
    gap: 8px 20px;

    /* The 80px spacer divs survive the wrap and indent each line differently; the gap spaces them. */
    > div:empty {
      display: none;
    }
  }
`

export default function ChargerGroups() {
  const { t } = useTranslation()
  const { theme } = useOutletContext<{ theme: string }>()
  const confirm = useConfirm()
  const { addNotification } = useNotification()
  const [searchParams, setSearchParams] = useSearchParams()

  const [chargerGroups, setChargerGroups] = useState<ChargerGroupsType | null>()
  const [openGroupName, setOpenGroupName] = useState<string | null>()
  const [bucketFiles, setBucketFiles] = useState<{ swVersion: string[], fwVersion: string[], osVersion: string[] }>({ swVersion: [], fwVersion: [], osVersion: [] })
  const scrollToGroupRef = useRef<(HTMLElement)[]>([])
  const [lastRefresh, setLastRefresh] = useState<string>('')

  const [editingGroup, setEditingGroup] = useState<string | null>() // Holds the charger group name when modal open
  const [isAddingGroup, setIsAddingGroup] = useState<boolean>(false)
  const [isMovingCharger, setIsMovingCharger] = useState<{ charger: string, chargerGroup: string } | null>()
  const [movingChargerSelectorValue, setMovingChargerSelectorValue] = useState<string | null>()

  const [OSVersionSelectorValue, setOSVersionSelectorValue] = useState<string>()
  const [SWVersionSelectorValue, setSWVersionSelectorValue] = useState<string>()
  const [FWVersionSelectorValue, setFWVersionSelectorValue] = useState<string>()

  const [editChargerGroup_SearchResults, setEditChargerGroup_SearchResults] = useState<string[]>([])
  const [editChargerGroup_AddedChargers, setEditChargerGroup_AddedChargers] = useState<string[]>([])

  const [editChargerGroup_NameInput, setEditChargerGroup_NameInput] = useState<string>()
  const [editChargerGroup_isLoading, setEditChargerGroup_isLoading] = useState<boolean>(false)

  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false)
  const [addedFilesList, setAddedFilesList] = useState<File[]>([])
  const [uploadedFilesList, setUploadedFilesList] = useState<File[]>([])
  const [dragDropFilesError, setDragDropFilesError] = useState<string | null>()
  const dragDropErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [globalSearchResults, setGlobalSearchResults] = useState<{ [key: string]: string[] } | null>({})
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>()
  const searchCacheRef = useRef<{ [key: string]: string }>({}) // chargerId -> normalized search string

  const [editingChargerSoftwareVerion, setEditingChargerSoftwareVerion] = useState<{ chargerId: string, software: string /* OS / SW / FW */ } | null>()
  const [filterDropdownValue, setFilterDropdownValue] = useState<string>('All')
  const [isShowingSearchHelp, setIsShowingSearchHelp] = useState<boolean>(false)

  const [isShowingEditGroupFilterModal, setIsShowingEditGroupFilterModal] = useState<boolean>(false)

  // Check for duplicate chargers, slows a little bit, should remove if well tested
  useEffect(() => {
    if (chargerGroups) {
      const chargerList: string[] = []
      for (const group in chargerGroups) {
        for (const charger in chargerGroups[group].chargers) {
          if (chargerList.includes(charger)) {
            console.log('Duplicate:', charger, group)
          } else {
            chargerList.push(charger)
          }
        }
      }
    }
  }, [chargerGroups])

  // Move all info@freecharger.hu chargers to their own group
  /*useEffect(() => {
    async function moveee() {
      if (chargerGroups) {

        for (const group in chargerGroups) {
          for (const charger in chargerGroups[group].chargers) {

            if (chargerGroups[group].chargers[charger].userEmail == 'info@freecharger.hu' && group !== 'Freecharger') {
              console.log('freeCharger')

              await set(ref(getDatabase(fbConf), `voltie/updategroups/Freecharger/chargers/${charger}`),
                {
                  fwVersion: chargerGroups[group].chargers[charger].fwVersion,
                  swVersion: chargerGroups[group].chargers[charger].swVersion,
                  osVersion: chargerGroups[group].chargers[charger].osVersion,
                  comments: chargerGroups[group].chargers[charger].comments || null
                }
              )
              await update(ref(getDatabase(fbConf)), { [`voltie/updategroups/${group}/chargers/${charger}`]: null })
              //await fetchChargerGroups()

            }

          }
        }

      }
    }
    moveee()
  }, [chargerGroups])*/

  async function fetchChargerGroups() {
    try {
      const dbRef = ref(getDatabase(fbConf))
      const response = await get(child(dbRef, 'voltie/updategroups'))
      const result = response.val() || {}

      const detailsResponse = await Comm('/api-charger/chargers', {})
      const details: ChargerType[] = await detailsResponse.json()

      const detailsObject: { [key: string]: { userEmail: string, online: boolean, osVersionNumber: string, swVersionNumber: string, fwVersionNumber: string, lastPresence: string, userId: string, localName: string } } = {}
      for (const charger of details) {
        detailsObject[charger.chargerId] = { userEmail: charger.userEmail, online: charger.online, osVersionNumber: charger.osVersion, swVersionNumber: charger.swVersion, fwVersionNumber: charger.fwVersion, lastPresence: charger.lastPresence, userId: charger.userId, localName: charger.localName }
      }

      // Fetch all online chargers so we can set the online field of chargers more accurately
      const onlineChargersResponse = await Comm('/api-websocket/online', {})
      const onlineChargers = await onlineChargersResponse.json()

      for (const group in result) {
        for (const charger in result[group].chargers) {
          // A charger can be in the Firebase group tree but missing from the charger API response
          // (deregistered/deleted charger, or a partial response — the firmware-update flow writes
          // chargers into the groups without syncing the charger API). Default its metadata instead
          // of dereferencing undefined, so a single orphan charger doesn't wedge the whole page (VLT-2168).
          const d = detailsObject[charger]
          result[group].chargers[charger].userEmail = d?.userEmail ?? ''
          result[group].chargers[charger].online = onlineChargers.includes(charger)
          result[group].chargers[charger].osVersionNumber = d?.osVersionNumber ?? ''
          result[group].chargers[charger].swVersionNumber = d?.swVersionNumber ?? ''
          result[group].chargers[charger].fwVersionNumber = d?.fwVersionNumber ?? ''
          result[group].chargers[charger].lastPresence = d?.lastPresence ?? ''
          result[group].chargers[charger].userId = d?.userId ?? ''
          result[group].chargers[charger].localName = d?.localName ?? ''
        }
      }

      // Pre-compute normalized search strings for fast text search
      const cache: { [key: string]: string } = {}
      for (const group in result) {
        for (const charger in result[group].chargers) {
          const c = result[group].chargers[charger]
          cache[charger] = normalizeString(`${charger} ${c.userEmail} ${c.userId} ${c.localName} fw=${c.fwVersionNumber} sw=${c.swVersionNumber} os=${c.osVersionNumber}`.toLowerCase())
        }
      }
      searchCacheRef.current = cache
      setChargerGroups(result)
    } catch (err) {
      // Never leave the page stuck on the loader on any field/network error: surface it and
      // resolve to a non-null (empty) state so the UI renders instead of spinning forever (VLT-2168).
      addNotification(`Error while loading charger groups: ${err}`, "TEXT", "error")
      setChargerGroups({})
    }
  }

  async function fetchBucketFiles() {
    const response = await Comm('/api-firmware/bucketfiles', {})
    const result = await response.json()
    console.log(result)
    setBucketFiles(result)
  }

  useEffect(() => {
    handleRefresh()
  }, [])

  async function handleRefresh() {
    setChargerGroups(null)
    setBucketFiles({ swVersion: [], fwVersion: [], osVersion: [] })
    fetchChargerGroups()
    fetchBucketFiles()
    setLastRefresh(new Date().toString().split(' ')[4])
  }

  useEffect(() => {
    const editGroupParam = searchParams.get('editGroup')
    if (editGroupParam && chargerGroups && chargerGroups[editGroupParam]) {
      handleEditGroupModalOpen(editGroupParam)
      searchParams.delete('editGroup')
      setSearchParams(searchParams, { replace: true })
    }
  }, [chargerGroups])

  function handleOpenGroup(groupName: string, groupIndex: number) {
    if (openGroupName == groupName) {
      setOpenGroupName(null)
    } else {
      setOpenGroupName(groupName)

      // Scroll to group
      setTimeout(() => {
        scrollToGroupRef.current[groupIndex].scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }

  function handleEditChargerGroup_Search(input: string) {
    if (chargerGroups) {
      if (input != '') {
        const result = []
        for (const charger in { ...chargerGroups['Default'].chargers, ...(editingGroup ? chargerGroups[editingGroup].chargers : []) }) {
          if (charger.toLocaleLowerCase().includes(input.toLocaleLowerCase())) {
            result.push(charger)
          }
        }
        setEditChargerGroup_SearchResults(result)
      } else {
        setEditChargerGroup_SearchResults(Object.keys({ ...chargerGroups['Default'].chargers, ...(editingGroup ? chargerGroups[editingGroup].chargers : []) }))
      }
    }
  }

  function handleEditChargerGroup_AddCharger(charger: string) {
    setEditChargerGroup_AddedChargers([...editChargerGroup_AddedChargers, charger])
  }

  function handleEditChargerGroup_RemoveCharger(charger: string) {
    const addedChargersWithoutCharger = [...editChargerGroup_AddedChargers]
    addedChargersWithoutCharger.splice(addedChargersWithoutCharger.indexOf(charger), 1)
    setEditChargerGroup_AddedChargers(addedChargersWithoutCharger)
  }

  async function handleAddNewGroupModalOpen() {
    if (chargerGroups) {
      setEditChargerGroup_SearchResults(Object.keys(chargerGroups['Default'].chargers))
      setEditChargerGroup_AddedChargers([])
      setIsAddingGroup(true)
    }
  }

  function handleEditGroupModalOpen(group: string) {
    if (chargerGroups) {
      setEditChargerGroup_SearchResults(Object.keys({ ...chargerGroups['Default'].chargers, ...chargerGroups[group].chargers }))
      setEditChargerGroup_AddedChargers(Object.keys(chargerGroups[group].chargers || {}))
      setEditingGroup(group)
    }
  }

  async function handleAddNewChargerGroup() {
    setEditChargerGroup_isLoading(true)
    if (chargerGroups) {
      if (editChargerGroup_NameInput) {

        const dbRef = ref(getDatabase(fbConf))
        const response = await get(child(dbRef, `voltie/updategroups/${editChargerGroup_NameInput}`))
        if (!response.val()) {

          const chargersToAdd: { [key: string]: { fwVersion: string, swVersion: string, osVersion: string, comments: { author: string, comment: string }[] } } = {}
          for (const charger of editChargerGroup_AddedChargers) {
            chargersToAdd[charger] = {
              fwVersion: chargerGroups['Default'].chargers[charger].fwVersion,
              swVersion: chargerGroups['Default'].chargers[charger].swVersion,
              osVersion: chargerGroups['Default'].chargers[charger].osVersion,
              comments: chargerGroups['Default'].chargers[charger].comments || null
            }
          }
          // Resolve platform versions for SW and OS
          const newGroupSwVersion = SWVersionSelectorValue != '-' && SWVersionSelectorValue ? resolveForSave(SWVersionSelectorValue, bucketFiles.swVersion) : ''
          const newGroupOsVersion = OSVersionSelectorValue != '-' && OSVersionSelectorValue ? resolveForSave(OSVersionSelectorValue, bucketFiles.osVersion) : ''

          try {
            await set(ref(getDatabase(fbConf), `voltie/updategroups/${editChargerGroup_NameInput}`), {
              chargers: chargersToAdd,
              groupOsVersion: newGroupOsVersion,
              groupSwVersion: newGroupSwVersion,
              groupFwVersion: FWVersionSelectorValue != '-' ? FWVersionSelectorValue : '',
              lastEdit: Date.now()
            })
          } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }

          const toDeleteFromDefault: { [key: string]: null } = {}
          for (const charger of editChargerGroup_AddedChargers) {
            toDeleteFromDefault[`voltie/updategroups/Default/chargers/${charger}`] = null
          }
          await update(dbRef, toDeleteFromDefault)


          await fetchChargerGroups()
          setEditingGroup(null)
          setIsAddingGroup(false)

        } else {
          console.log('TODO: show error: charger group already exists')
        }
      } else {
        console.log('TODO: show error: name cant be empty')
      }
    }
    setEditChargerGroup_isLoading(false)
  }

  async function handleSaveEditedChargerGroup() {
    setEditChargerGroup_isLoading(true)
    if (chargerGroups && editingGroup) {

      // Add all now selected chargers to group
      const chargersToAdd: { [key: string]: { fwVersion: string, swVersion: string, osVersion: string, comments: { author: string, comment: string }[] } } = {}
      for (const charger of editChargerGroup_AddedChargers) {
        if (chargerGroups['Default'].chargers[charger]) {
          chargersToAdd[charger] = {
            fwVersion: chargerGroups['Default'].chargers[charger].fwVersion,
            swVersion: chargerGroups['Default'].chargers[charger].swVersion,
            osVersion: chargerGroups['Default'].chargers[charger].osVersion,
            comments: chargerGroups['Default'].chargers[charger].comments || null
          }

        } else if (chargerGroups[editingGroup].chargers[charger]) {
          chargersToAdd[charger] = {
            fwVersion: chargerGroups[editingGroup].chargers[charger].fwVersion,
            swVersion: chargerGroups[editingGroup].chargers[charger].swVersion,
            osVersion: chargerGroups[editingGroup].chargers[charger].osVersion,
            comments: chargerGroups[editingGroup].chargers[charger].comments || null
          }
        }
      }
      // Resolve platform versions for SW and OS
      const resolvedSwVersion = SWVersionSelectorValue != '-' && SWVersionSelectorValue ? resolveForSave(SWVersionSelectorValue, bucketFiles.swVersion) : ''
      const resolvedOsVersion = OSVersionSelectorValue != '-' && OSVersionSelectorValue ? resolveForSave(OSVersionSelectorValue, bucketFiles.osVersion) : ''

      try {
        await set(ref(getDatabase(fbConf), `voltie/updategroups/${editingGroup}`), {
          chargers: chargersToAdd,
          groupOsVersion: resolvedOsVersion,
          groupSwVersion: resolvedSwVersion,
          groupFwVersion: FWVersionSelectorValue != '-' ? FWVersionSelectorValue : '',
          frontendOrder: chargerGroups[editingGroup].frontendOrder || null,
          lastEdit: Date.now(),
          frontendFilter: chargerGroups[editingGroup].frontendFilter || null
        })
      } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }

      // Add all previous chargers to Default
      const toAddToDefault: { [key: string]: { fwVersion: string, swVersion: string, osVersion: string, comments: { author: string, comment: string }[] } } = {}
      for (const charger in chargerGroups[editingGroup].chargers) {
        //toAddToDefault[`voltie/updategroups/Default/chargers/${charger}`] = chargerGroups[editingGroup].chargers[charger]
        toAddToDefault[`voltie/updategroups/Default/chargers/${charger}`] = {
          fwVersion: chargerGroups[editingGroup].chargers[charger].fwVersion,
          swVersion: chargerGroups[editingGroup].chargers[charger].swVersion,
          osVersion: chargerGroups[editingGroup].chargers[charger].osVersion,
          comments: chargerGroups[editingGroup].chargers[charger].comments || null
        }
      }
      try {
        await update(ref(getDatabase(fbConf)), toAddToDefault)
      } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }

      // Delete all now selected chargers from default (not if the group in question is the Default)
      if (editingGroup !== 'Default') {
        const toDeleteFromDefault: { [key: string]: null } = {}
        for (const charger of editChargerGroup_AddedChargers) {
          toDeleteFromDefault[`voltie/updategroups/Default/chargers/${charger}`] = null
        }
        try {
          await update(ref(getDatabase(fbConf)), toDeleteFromDefault)
        } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }
      }

      await fetchChargerGroups()
      setEditingGroup(null)
      setIsAddingGroup(false)


      // Show alert that custom versioned softwares wont get the group software
      const chargerIdsWithCustomSoftwareVerisons: string[] = []
      for (const chargerId in chargersToAdd) {
        if (chargersToAdd[chargerId].fwVersion != '' || chargersToAdd[chargerId].swVersion != '' || chargersToAdd[chargerId].osVersion != '') {
          chargerIdsWithCustomSoftwareVerisons.push(chargerId)
        }
      }
      if (chargerIdsWithCustomSoftwareVerisons.length > 0) {
        await confirm({ message: `Töltők egyedileg beállított verzióval nem kapják meg a csoportos verziókat. (${chargerIdsWithCustomSoftwareVerisons.join(', ')})`, showCancel: false })
      }


    }
    setEditChargerGroup_isLoading(false)

    handleRefreshFirebaseToServerConnection()
  }

  function handleChargerMenuSelect(value: string, charger: string, chargerGroup: string) {
    if (chargerGroups) {
      switch (value) {
        case t('chargerGroups_forceUpdateCheck'):
          handleForceUpdateCharger(charger)
          break;
        case `${t('chargerGroups_reactivate')} (${t('general_all')})`:
          handleResetCharger(charger, chargerGroup, true, true, true)
          break;
        case t('chargerGroups_move'):
          setIsMovingCharger({ charger, chargerGroup })
          break;
      }
    }
  }

  async function handleForceUpdateCharger(chargerId: string) {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 15, "sub_cmd": 1 })
    })
    if (response.ok) {
      addNotification(JSON.stringify(await response.json()), "JSON")
    } else {
      addNotification(await response.text(), "TEXT", "error")
    }
  }

  async function handleResetCharger(chargerId: string, group: string, fwReset?: boolean, swReset?: boolean, osReset?: boolean) {
    if (chargerGroups) {
      const chargerData = chargerGroups[group].chargers[chargerId]
      if (swReset && !chargerData?.swVersion && !chargerGroups[group].groupSwVersion) {
        addNotification('No SW version set for this charger or its group', "TEXT", "warning")
      }
      if (fwReset && !chargerData?.fwVersion && !chargerGroups[group].groupFwVersion) {
        addNotification('No FW version set for this charger or its group', "TEXT", "warning")
      }
      if (osReset && !chargerData?.osVersion && !chargerGroups[group].groupOsVersion) {
        addNotification('No OS version set for this charger or its group', "TEXT", "warning")
      }
    }
    const response = await Comm('/api-firmware/v2/update/reset', {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        "chargerId": chargerId,
        "fwReset": fwReset || false,
        "swReset": swReset || false,
        "osReset": osReset || false
      })
    })
    if (response.ok) {
      addNotification(JSON.stringify(await response.json()), "JSON")
    } else {
      addNotification(await response.text(), "TEXT", "error")
    }
  }

  async function handleMoveCharger() {
    if (chargerGroups && isMovingCharger) {

      if (movingChargerSelectorValue == isMovingCharger.chargerGroup) {
        addNotification("You can't move a charger to the group its already in", 'TEXT', 'warning')
        return
      }
      try {
        await set(ref(getDatabase(fbConf), `voltie/updategroups/${movingChargerSelectorValue}/chargers/${isMovingCharger.charger}`),
          {
            fwVersion: chargerGroups[isMovingCharger.chargerGroup].chargers[isMovingCharger.charger].fwVersion,
            swVersion: chargerGroups[isMovingCharger.chargerGroup].chargers[isMovingCharger.charger].swVersion,
            osVersion: chargerGroups[isMovingCharger.chargerGroup].chargers[isMovingCharger.charger].osVersion,
            comments: chargerGroups[isMovingCharger.chargerGroup].chargers[isMovingCharger.charger].comments || null
          }
        )
      } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }

      try {
        await update(ref(getDatabase(fbConf)), { [`voltie/updategroups/${isMovingCharger.chargerGroup}/chargers/${isMovingCharger.charger}`]: null })
      } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }
      setIsMovingCharger(null)
      await fetchChargerGroups()
    }
  }

  function getFileInfo(fileName: string): { icon: string, label: string, apiType: string } {
    switch (fileName.split('.').at(-1)) {
      case 'vlt': return { icon: 'chargerGroups.OS', label: t('chargerGroups_operatingSystem'), apiType: 'os' }
      case 'bin': return { icon: 'chargerGroups.FW', label: t('chargerGroups_firmwareSystem'), apiType: 'firmware' }
      default: return { icon: 'chargerGroups.SW', label: t('chargerGroups_defaultSoftware'), apiType: 'software' }
    }
  }

  function setDragDropError(msg: string | null) {
    if (dragDropErrorTimerRef.current) clearTimeout(dragDropErrorTimerRef.current)
    setDragDropFilesError(msg)
    if (msg) {
      dragDropErrorTimerRef.current = setTimeout(() => setDragDropFilesError(null), 4000)
    }
  }

  function handleRemoveFileFromUploadingFiles(fileName: string) {
    const newFiles = []
    for (const file of addedFilesList) {
      if (file.name != fileName) {
        newFiles.push(file)
      }
    }
    setAddedFilesList(newFiles)
  }

  function handleAddFileToUploadingFiles(fileList: File[]) {
    const approvedFileList: File[] = []
    const ext = (f: File) => f.name.split('.').at(-1)

    for (const file of fileList) {
      const isSupported = ext(file) === 'vlt' || ext(file) === 'bin' || ext(file) === file.name
      if (isSupported) {
        if (!addedFilesList.find(e => e.name === file.name)) {
          approvedFileList.push(file)
        } else {
          setDragDropError(t('chargerGroups_fileAlreadySelected'))
        }
      } else {
        setDragDropError(fileList.length > 1 ? t('chargerGroups_oneOrMoreFilesWithUnsupportedFileType') : t('chargerGroups_unsupportedFileType'))
      }
    }

    if (approvedFileList.length > 0 && approvedFileList.length === fileList.length) {
      setDragDropError(null)
    }
    setAddedFilesList(prev => [...approvedFileList, ...prev])
  }

  function handleDropFiles(e: DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingOver(false)
    if (e.dataTransfer?.files) {
      handleAddFileToUploadingFiles(Array.from(e.dataTransfer.files))
    }
  }

  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleAddFileToUploadingFilesWithFilePicker() {
    fileInputRef.current?.click()
  }

  async function handleUploadFiles() {
    if (isUploading) return
    setIsUploading(true)

    const results = await Promise.all(addedFilesList.map(async (file) => {
      const formdata = new FormData()
      formdata.append("file", file, file.name)
      const response = await Comm(`/api-firmware/upload?type=${getFileInfo(file.name).apiType}`, {
        method: "POST",
        body: formdata
      })
      return { file, ok: response.status === 200 }
    }))

    const succeeded = results.filter(r => r.ok).map(r => r.file)
    const failed = results.filter(r => !r.ok).map(r => r.file)

    failed.forEach(f => addNotification(`Upload failed: ${f.name}`, "TEXT", "error"))
    if (succeeded.length > 0) addNotification(`${succeeded.length} file${succeeded.length > 1 ? 's' : ''} uploaded successfully`, "TEXT")

    setUploadedFilesList(prev => [...succeeded, ...prev])
    setAddedFilesList(failed)
    setIsUploading(false)
  }

  function parseSingleFilter(input: string): { chargerFilter?: (c: ChargerType, chargerId: string) => boolean, groupFilter?: (group: string) => boolean } {
    const trimmed = input.trim()

    // Compare version strings segment by segment (e.g. 1.2.52 vs 1.2.53)
    function compareVersions(a: string, b: string): number {
      const aParts = a.split('.').map(n => parseInt(n) || 0)
      const bParts = b.split('.').map(n => parseInt(n) || 0)
      const len = Math.max(aParts.length, bParts.length)
      for (let i = 0; i < len; i++) {
        const diff = (aParts[i] || 0) - (bParts[i] || 0)
        if (diff !== 0) return diff
      }
      return 0
    }

    // Version comparison: fw>=301, sw<1.2.52, os=13, fw!=289
    const versionMatch = trimmed.match(/^(fw|sw|os)(>=|<=|!=|>|<|=)(.+)$/)
    if (versionMatch) {
      const [, field, operator, value] = versionMatch
      const getVersion = (c: ChargerType) => {
        return (field === 'fw' ? c.fwVersionNumber : field === 'sw' ? c.swVersionNumber : c.osVersionNumber) || '0'
      }
      const fn = (c: ChargerType) => {
        const cmp = compareVersions(getVersion(c), value)
        switch (operator) {
          case '>': return cmp > 0
          case '<': return cmp < 0
          case '>=': return cmp >= 0
          case '<=': return cmp <= 0
          case '=': return cmp === 0
          case '!=': return cmp !== 0
          default: return false
        }
      }
      return { chargerFilter: fn }
    }

    // Status filter: online, offline
    if (trimmed.toLowerCase() === 'online') return { chargerFilter: (c) => c.online === true }
    if (trimmed.toLowerCase() === 'offline') return { chargerFilter: (c) => c.online === false }

    // Group filter: group=Default, group!=Betatester
    const groupMatch = trimmed.match(/^group(!=|=)(.+)$/i)
    if (groupMatch) {
      const [, operator, value] = groupMatch
      const groupName = value.toLowerCase()
      if (operator === '=') {
        return { groupFilter: (group) => normalizeString(group.toLowerCase()).includes(normalizeString(groupName)) }
      } else {
        return { groupFilter: (group) => !normalizeString(group.toLowerCase()).includes(normalizeString(groupName)) }
      }
    }

    // No version set: nosw, nofw, noos
    if (trimmed.toLowerCase() === 'nosw') return { chargerFilter: (c) => !c.swVersionNumber || c.swVersionNumber === '' }
    if (trimmed.toLowerCase() === 'nofw') return { chargerFilter: (c) => !c.fwVersionNumber || c.fwVersionNumber === '' }
    if (trimmed.toLowerCase() === 'noos') return { chargerFilter: (c) => !c.osVersionNumber || c.osVersionNumber === '' }
    if (trimmed.toLowerCase() === 'hassw') return { chargerFilter: (c) => !!c.swVersionNumber && c.swVersionNumber !== '' }
    if (trimmed.toLowerCase() === 'hasfw') return { chargerFilter: (c) => !!c.fwVersionNumber && c.fwVersionNumber !== '' }
    if (trimmed.toLowerCase() === 'hasos') return { chargerFilter: (c) => !!c.osVersionNumber && c.osVersionNumber !== '' }

    // Unrecognized filter — treat as plain text search using pre-computed cache
    const normalizedInput = normalizeString(trimmed.toLowerCase())
    return { chargerFilter: (_c, chargerId) => {
      return (searchCacheRef.current[chargerId] || '').includes(normalizedInput)
    }}
  }

  function handleGlobalSearch(input: string) {
    if (chargerGroups) {
      if (input != '') {
        // Split by & to support multiple filters
        const parts = input.split('&').map(p => p.trim()).filter(Boolean)
        const filters = parts.map(p => parseSingleFilter(p))

        const results: { [key: string]: string[] } = {}
        for (const group in chargerGroups) {
          // Check group filters
          const groupFilters = filters.filter(f => f.groupFilter)
          if (groupFilters.length > 0 && !groupFilters.every(f => f.groupFilter!(group))) continue

          for (const charger in chargerGroups[group].chargers) {
            const chargerData = chargerGroups[group].chargers[charger]
            const chargerFilters = filters.filter(f => f.chargerFilter)
            const matches = chargerFilters.length > 0 ? chargerFilters.every(f => f.chargerFilter!(chargerData, charger)) : true

            if (matches) {
              if (!results[group]) results[group] = []
              results[group].push(charger)
            }
          }
        }
        if (Object.keys(results).length < 1) {
          setGlobalSearchResults(null)
        } else {
          setGlobalSearchResults(results)
        }
      } else {
        setGlobalSearchResults({})
      }
    }
  }

  function isVersionMismatch(groupFileName: string, chargerFileName: string, version: string, type: string /* SW / FW */) {
    try {
      let compareAgainst
      if (chargerFileName && chargerFileName != ' ') {
        compareAgainst = chargerFileName
      } else if (groupFileName && groupFileName !== ' ') {
        compareAgainst = groupFileName
      } else {
        return false
      }

      if (type == 'SW') {
        if (compareAgainst.split('_v')[1].split('_')[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.').replace(/\.0/g, '.').replace(/\.0/g, '.') == version) {
          return false
        } else {
          return true
        }
      } else if (type == 'FW') {
        if (compareAgainst.split('_v')[1].split('.')[0].split('_')[0] == version.replace(/\./g, '')) {
          return false
        } else {
          return true
        }
      }
    } catch (err) {
      console.log(err, groupFileName, chargerFileName, version, type)
      return false
    }
  }

  async function handleGroupSwVersionSelect(value: string) {
    const currentValue = editingGroup ? stripPlatform(chargerGroups?.[editingGroup]?.groupSwVersion || '') : ''
    if (value && value != '-' && value != currentValue && displayNameHadPlatform(value, bucketFiles.swVersion)) {
      const missing = getMissingPlatforms(value, bucketFiles.swVersion)
      if (missing.length > 0) {
        await confirm({ message: `SW version is not available for all platforms (missing: ${missing.join(', ')}).`, showCancel: false })
      }
    }
    setSWVersionSelectorValue(value)
  }

  async function handleGroupOsVersionSelect(value: string) {
    const currentValue = editingGroup ? stripPlatform(chargerGroups?.[editingGroup]?.groupOsVersion || '') : ''
    if (value && value != '-' && value != currentValue && displayNameHadPlatform(value, bucketFiles.osVersion)) {
      const missing = getMissingPlatforms(value, bucketFiles.osVersion)
      if (missing.length > 0) {
        await confirm({ message: `OS version is not available for all platforms (missing: ${missing.join(', ')}).`, showCancel: false })
      }
    }
    setOSVersionSelectorValue(value)
  }

  async function handleEditChargerSoftwareVersion(chargerId: string, group: string, newSoftware: string, updatingSoftware: string) {
    // @ts-expect-error: Should create an enum for updatingSoftware, this is a TODO
    const currentValue = chargerGroups?.[group]?.chargers[chargerId]?.[updatingSoftware] || ''
    if (chargerGroups && newSoftware != '' && stripPlatform(currentValue) != newSoftware) {

      if (newSoftware == 'Delete') {
        newSoftware = ''
      }

      // Resolve platform version for SW and OS
      if (newSoftware != '' && (updatingSoftware == 'swVersion' || updatingSoftware == 'osVersion')) {
        const allFiles = updatingSoftware == 'swVersion' ? bucketFiles.swVersion : bucketFiles.osVersion
        if (displayNameHadPlatform(newSoftware, allFiles)) {
          const missing = getMissingPlatforms(newSoftware, allFiles)
          if (missing.length > 0) {
            const proceed = await confirm(`This version is not available for all platforms (missing: ${missing.join(', ')}). Continue anyway?`)
            if (!proceed) {
              setEditingChargerSoftwareVerion(null)
              return
            }
          }
          newSoftware = resolveForSave(newSoftware, allFiles)
        }
      }

      try {
        await set(ref(getDatabase(fbConf), `voltie/updategroups/${group}/chargers/${chargerId}/${updatingSoftware}`), newSoftware)
        await set(ref(getDatabase(fbConf), `voltie/updategroups/${group}/lastEdit`), Date.now())
      } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }

      // @ts-expect-error: Should create an enum for updatingSoftware, this is a TODO
      chargerGroups[group].chargers[chargerId][updatingSoftware] = newSoftware
      chargerGroups[group].lastEdit = Date.now() as unknown as string
      setEditingChargerSoftwareVerion(null)
      handleRefreshFirebaseToServerConnection()
    }
  }

  async function handleUpdateGroupFilter(group: string) {
    try {
      if (chargerGroups && group) {


        const filterRegex = /^include='[^']*'(;exclude='[^']*')?$|^exclude='[^']*'(;include='[^']*')?$/;
        if ((chargerGroups[group].frontendFilter?.FW == "" || chargerGroups[group].frontendFilter?.FW == undefined || filterRegex.test(chargerGroups[group].frontendFilter?.FW || "")) &&
          (chargerGroups[group].frontendFilter?.SW == "" || chargerGroups[group].frontendFilter?.SW == undefined || filterRegex.test(chargerGroups[group].frontendFilter?.SW || "")) &&
          (chargerGroups[group].frontendFilter?.OS == "" || chargerGroups[group].frontendFilter?.OS == undefined || filterRegex.test(chargerGroups[group].frontendFilter?.OS || ""))) {

          await set(ref(getDatabase(fbConf), `voltie/updategroups/${group}/frontendFilter`), { FW: chargerGroups[group].frontendFilter?.FW || "", SW: chargerGroups[group].frontendFilter?.SW || "", OS: chargerGroups[group].frontendFilter?.OS || "" })
        } else {
          addNotification("Invalid filter format", "TEXT", "error")
          handleRefresh()
          setIsShowingEditGroupFilterModal(true)
        }
      } else {
        addNotification("Error while updating filter", "TEXT", "error")
      }
    } catch {
      addNotification("Error while updating filter", "TEXT", "error")
    }
  }

  function checkIfFilePassesGroupFilter(fileName: string, filter: string): boolean {
    try {
      if (!fileName || !filter || fileName == "" || filter == "") {
        return true
      }

      // Parse filter: include='_a,_b';exclude='_c'
      const parts = Object.fromEntries(
        filter.split(";").map(p => p.trim()).filter(Boolean).map(p => {
          const [key, value] = p.split("=")
          return [key.trim(), value.replace(/^'|'$/g, "").split(",")]
        })
      )

      const include = parts.include ?? []
      const exclude = parts.exclude ?? []

      // Must contain ALL include substrings
      for (const inc of include) {
        if (inc && !fileName.includes(inc)) return false
      }

      // Must contain NONE of the exclude substrings
      for (const exc of exclude) {
        if (exc && fileName.includes(exc)) return false
      }

      return true
    } catch {
      return true
    }
  }

  // Refreshing the servers connections to Firebase, should be called after every sortware update
  async function handleRefreshFirebaseToServerConnection() {
    await Comm('/api-firmware/v2/update/maintain/refresh', {})
  }

  /* Admin maintenance (one-off): move every Default-group charger that has a user assigned into
  // the Installed_chargers group, then log the ones left in Default that still have a local name. Both
  // come from the charger-API enrichment in fetchChargerGroups: userId is '' or 'UNKNOWN' without a user,
  // and localName is '' or ' ' when unset, or the charger id itself, which the backend puts back on a reset.
  async function moveChargersWithUserFromDefaultToInstalled() {
    if (!chargerGroups) return

    const defaultChargers = chargerGroups['Default']?.chargers
    if (!defaultChargers) {
      addNotification('No chargers in the Default group', 'TEXT', 'warning')
      return
    }

    const hasUser = (charger: string) => {
      const userId = defaultChargers[charger].userId
      return !!userId && userId !== 'UNKNOWN'
    }
    const hasLocalName = (charger: string) => {
      const name = (defaultChargers[charger].localName || '').trim()
      return !!name && name !== charger && name !== 'UNKNOWN'
    }
    const logLeftWithLocalName = () => {
      const left = Object.keys(defaultChargers).filter(charger => !hasUser(charger) && hasLocalName(charger))
      console.log(`${left.length} charger(s) left in Default with a local name:`)
      console.table(left.map(charger => ({ chargerId: charger, localName: defaultChargers[charger].localName })))
    }

    const chargersToMove = Object.keys(defaultChargers).filter(hasUser)
    if (chargersToMove.length === 0) {
      addNotification('No chargers with a user in the Default group', 'TEXT', 'warning')
      logLeftWithLocalName()
      return
    }

    const proceed = await confirm(`Move ${chargersToMove.length} charger(s) with a user from Default to Installed_chargers?`)
    if (!proceed) return

    // Single atomic multi-path update: write each persisted node into Installed_chargers and null it in Default.
    const updates: Record<string, unknown> = {}
    for (const charger of chargersToMove) {
      updates[`voltie/updategroups/Installed_chargers/chargers/${charger}`] = {
        fwVersion: defaultChargers[charger].fwVersion,
        swVersion: defaultChargers[charger].swVersion,
        osVersion: defaultChargers[charger].osVersion,
        comments: defaultChargers[charger].comments || null
      }
      updates[`voltie/updategroups/Default/chargers/${charger}`] = null
    }

    try {
      await update(ref(getDatabase(fbConf)), updates)
      addNotification(`Moved ${chargersToMove.length} charger(s) from Default to Installed_chargers`, 'TEXT')
    } catch (err) {
      addNotification(`Unexpected response: ${err}`, 'TEXT', 'error')
      return
    }
    logLeftWithLocalName()
    await fetchChargerGroups()
  }*/

  return (
    <>
      {/*<button onClick={moveChargersWithUserFromDefaultToInstalled}>Move chargers with a user from Default to Installed_chargers</button>*/}
      {isShowingSearchHelp && (
        <Modal onEscape={() => setIsShowingSearchHelp(false)}>
          <SmallModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingSearchHelp(false)} />
            </ModalCloseButtonContainer>
            <SmallModalLabel>Keresés</SmallModalLabel>
            <WhiteSpace height="20px" />

            <p style={{ fontSize: '12px', fontWeight: 600, color: '#3F4254', margin: '0 0 6px 0' }}>Szöveges keresés</p>
            <p style={{ fontSize: '11px', color: '#7E8299', margin: '0 0 12px 0' }}>ChargerID, email, local név, UserID, stb. alapján</p>

            <p style={{ fontSize: '12px', fontWeight: 600, color: '#3F4254', margin: '0 0 6px 0' }}>Verzió szűrők</p>
            <p style={{ fontSize: '11px', color: '#7E8299', margin: '0 0 4px 0' }}>Prefixek: <span style={{ fontWeight: 600 }}>fw</span>, <span style={{ fontWeight: 600 }}>sw</span>, <span style={{ fontWeight: 600 }}>os</span></p>
            <p style={{ fontSize: '11px', color: '#7E8299', margin: '0 0 8px 0' }}>Operátorok: <span style={{ fontWeight: 600 }}>=</span>, <span style={{ fontWeight: 600 }}>!=</span>, <span style={{ fontWeight: 600 }}>{'>'}</span>, <span style={{ fontWeight: 600 }}>{'<'}</span>, <span style={{ fontWeight: 600 }}>{'>='}</span>, <span style={{ fontWeight: 600 }}>{'<='}</span></p>
            {[
              { example: 'os=14', description: 'OS pontosan 14' },
              { example: 'fw>3.01', description: 'FW nagyobb mint 3.01' },
              { example: 'sw<=1.2.52', description: 'SW max 1.2.52' },
              { example: 'fw!=2.89', description: 'FW nem 2.89' },
            ].map(item => (
              <div key={item.example} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#4AB1FC', backgroundColor: '#ECF8FF', borderRadius: '6px', padding: '3px 8px', minWidth: '100px', fontFamily: 'monospace' }}>{item.example}</span>
                <span style={{ fontSize: '11px', color: '#7E8299', marginLeft: '12px' }}>{item.description}</span>
              </div>
            ))}
            <WhiteSpace height="12px" />

            <p style={{ fontSize: '12px', fontWeight: 600, color: '#3F4254', margin: '0 0 8px 0' }}>Státusz és verzió jelenlét</p>
            {[
              { example: 'online', description: 'Online töltők' },
              { example: 'offline', description: 'Offline töltők' },
              { example: 'hassw', description: 'Van SW verzió beállítva' },
              { example: 'nosw', description: 'Nincs SW verzió beállítva' },
            ].map(item => (
              <div key={item.example} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#4AB1FC', backgroundColor: '#ECF8FF', borderRadius: '6px', padding: '3px 8px', minWidth: '100px', fontFamily: 'monospace' }}>{item.example}</span>
                <span style={{ fontSize: '11px', color: '#7E8299', marginLeft: '12px' }}>{item.description}</span>
              </div>
            ))}
            <p style={{ fontSize: '10px', color: '#A1A5B7', margin: '4px 0 0 0' }}>Továbbá: nofw, noos, hasfw, hasos</p>
            <WhiteSpace height="12px" />

            <p style={{ fontSize: '12px', fontWeight: 600, color: '#3F4254', margin: '0 0 8px 0' }}>Csoport szűrők</p>
            {[
              { example: 'group=Beta', description: 'Csak az adott csoportban lévő töltők' },
              { example: 'group!=Default', description: 'Csoport kizárása' },
            ].map(item => (
              <div key={item.example} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#4AB1FC', backgroundColor: '#ECF8FF', borderRadius: '6px', padding: '3px 8px', minWidth: '100px', fontFamily: 'monospace' }}>{item.example}</span>
                <span style={{ fontSize: '11px', color: '#7E8299', marginLeft: '12px' }}>{item.description}</span>
              </div>
            ))}
            <WhiteSpace height="12px" />

            <p style={{ fontSize: '12px', fontWeight: 600, color: '#3F4254', margin: '0 0 6px 0' }}>Szűrők kombinálása</p>
            <p style={{ fontSize: '11px', color: '#7E8299', margin: '0 0 8px 0' }}><span style={{ fontWeight: 700 }}>&</span> jellel tetszőleges számú szűrő kombinálható (mindnek teljesülnie kell)</p>
            {[
              { example: 'os>=14 & online', description: 'Online, OS 14+' },
              { example: 'sw<10.03 & hasfw & group!=Default', description: 'Régi SW, van FW, nem Default csoportban' },
            ].map(item => (
              <div key={item.example} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#4AB1FC', backgroundColor: '#ECF8FF', borderRadius: '6px', padding: '3px 8px', minWidth: '220px', fontFamily: 'monospace' }}>{item.example}</span>
                <span style={{ fontSize: '11px', color: '#7E8299', marginLeft: '12px' }}>{item.description}</span>
              </div>
            ))}
            <WhiteSpace height="12px" />

            <p style={{ fontSize: '12px', fontWeight: 600, color: '#3F4254', margin: '0 0 8px 0' }}>Példák</p>
            {[
              { example: 'kovacs', description: 'Bármely mező tartalmazza: "kovacs"' },
              { example: 'offline', description: 'Összes offline töltő' },
              { example: 'os<13', description: 'Régi OS-t futtató töltők' },
              { example: 'kovacs & online', description: 'Tartalmazza "kovacs" és online' },
              { example: 'fw>=3.05 & online', description: 'Online töltők friss firmware-rel' },
              { example: 'gmail & os>=14 & hassw', description: 'Tartalmazza "gmail", OS 14+, van SW' },
              { example: 'nosw & group=Installed_chargers', description: 'SW nélküli töltők az Installed csoportban' },
              { example: 'os>=14 & sw>=1.3.1 & online', description: 'Online, friss OS és SW' },
              { example: 'fw<3.01 & hassw & group!=Default & offline', description: 'Offline, régi FW, van SW, nem Default csoportban' },
            ].map(item => (
              <div key={item.example} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#4AB1FC', backgroundColor: '#ECF8FF', borderRadius: '6px', padding: '3px 8px', minWidth: '280px', fontFamily: 'monospace' }}>{item.example}</span>
                <span style={{ fontSize: '11px', color: '#7E8299', marginLeft: '12px' }}>{item.description}</span>
              </div>
            ))}
          </SmallModalContainer>
        </Modal>
      )}
      {isShowingEditGroupFilterModal && chargerGroups && editingGroup && (
        <Modal onEscape={() => setIsShowingEditGroupFilterModal(false)}>
          <SmallModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingEditGroupFilterModal(false)} />
            </ModalCloseButtonContainer>
            <SmallModalLabel>Edit Group Filter</SmallModalLabel>
            <WhiteSpace height="16px" />
            <InputWithLabel label={"SW"} width={"100%"} value={chargerGroups[editingGroup].frontendFilter?.SW || "include='';exclude=''"} placeholder="include='_a,_b';exclude='_c'"
              onInput={(value) => setChargerGroups(prev => { if (!prev || !editingGroup) return prev; const copy = structuredClone(prev) as ChargerGroupsType; copy[editingGroup].frontendFilter = { ...(copy[editingGroup].frontendFilter || {}), SW: value }; return copy; })} />
            <WhiteSpace height="12px" />
            <InputWithLabel label={"FW"} width={"100%"} value={chargerGroups[editingGroup].frontendFilter?.FW || ""} placeholder="include='_a,_b';exclude='_c'"
              onInput={(value) => setChargerGroups(prev => { if (!prev || !editingGroup) return prev; const copy = structuredClone(prev) as ChargerGroupsType; copy[editingGroup].frontendFilter = { ...(copy[editingGroup].frontendFilter || {}), FW: value }; return copy; })} />
            <WhiteSpace height="12px" />
            <InputWithLabel label={"OS"} width={"100%"} value={chargerGroups[editingGroup].frontendFilter?.OS || ""} placeholder="include='_a,_b';exclude='_c'"
              onInput={(value) => setChargerGroups(prev => { if (!prev || !editingGroup) return prev; const copy = structuredClone(prev) as ChargerGroupsType; copy[editingGroup].frontendFilter = { ...(copy[editingGroup].frontendFilter || {}), OS: value }; return copy; })} />
            <WhiteSpace height="12px" />
            <SmallModalButtonContainer>
              <Button onclick={() => setIsShowingEditGroupFilterModal(false)} label={t('charger_cancel')} width="calc(50% - 8px)" />
              <WhiteSpace width="16px" />
              <Button onclick={() => { setIsShowingEditGroupFilterModal(false); handleUpdateGroupFilter(editingGroup) }} label={t('charger_save')} width="calc(50% - 8px)" bgcolor="#4AB1FC" labelcolor="white" />
            </SmallModalButtonContainer>
          </SmallModalContainer>
        </Modal>
      )}
      {isMovingCharger && chargerGroups && (
        <Modal onEscape={() => setIsMovingCharger(null)}>
          <SmallModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsMovingCharger(null)} />
            </ModalCloseButtonContainer>
            <SmallModalLabel>{t('chargerGroups_whereToMove')}</SmallModalLabel>
            <WhiteSpace height="32px" />
            <Select handleSelect={(value) => setMovingChargerSelectorValue(value)} width="100%" icon="general.charger" label={t('chargerGroups_chargerGroup')} options={Object.keys(chargerGroups)} />
            <WhiteSpace height="32px" />
            <SmallModalButtonContainer>
              <Button onclick={handleMoveCharger} label={t('chargerGroups_move')} width="calc(50% - 8px)" bgcolor="#4AB1FC" labelcolor="white" />
              <WhiteSpace width="16px" />
              <Button onclick={() => setIsMovingCharger(null)} label={t('chargerGroups_cancel')} width="calc(50% - 8px)" />
            </SmallModalButtonContainer>
          </SmallModalContainer>
        </Modal>
      )}
      {isUploadingFiles && (
        <Modal onEscape={() => { setIsUploadingFiles(false); setUploadedFilesList([]) }}>
          <BigModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => { setIsUploadingFiles(false); setUploadedFilesList([]) }} />
            </ModalCloseButtonContainer>
            <UploadFilesTitle>{t('chargerGroups_fileUpload')}</UploadFilesTitle>
            <WhiteSpace height="24px" />
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => { if (e.target.files) handleAddFileToUploadingFiles(Array.from(e.target.files)); e.target.value = '' }} />
            <FileDropContainer $isDraggingOver={isDraggingOver} onDrop={(e) => handleDropFiles(e as unknown as DragEvent)} onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true) }} onDragLeave={() => setIsDraggingOver(false)} onClick={handleAddFileToUploadingFilesWithFilePicker}>
              <WhiteSpace height="16px" />
              <Icon path="chargerGroups.fileDropUpload" width="48px" height="48px" marginleft="calc((100% - 48px) / 2)" />
              <WhiteSpace height="8px" />
              <FileDropLabel>{t('chargerGroups_dragDropFilesOrBrowse')}</FileDropLabel>
              <WhiteSpace height="16px" />
              {dragDropFilesError && (
                <>
                  <DragDropFilesErrorLabel>{dragDropFilesError}</DragDropFilesErrorLabel>
                  <WhiteSpace height="16px" />
                </>
              )}
            </FileDropContainer>
            <WhiteSpace height="32px" />
            <UploadFilesSubTitle>{t('chargerGroups_selected')}</UploadFilesSubTitle>
            <WhiteSpace height="16px" />
            {addedFilesList.map(file => {
              const info = getFileInfo(file.name)
              return (
                <React.Fragment key={file.name}>
                  <ListItemWithDeleteButton theme={theme} width="calc(100% - 24px)" icon={info.icon} label={info.label} value={file.name} handleDelete={() => handleRemoveFileFromUploadingFiles(file.name)} />
                  <WhiteSpace height="16px" />
                </React.Fragment>
              )
            })}
            <UploadFilesSubTitle>{t('chargerGroups_uploaded')}</UploadFilesSubTitle>
            <WhiteSpace height="16px" />
            {uploadedFilesList.map(file => {
              const info = getFileInfo(file.name)
              return (
                <React.Fragment key={file.name}>
                  <ListItem theme={theme} width="calc(100% - 24px)" icon={info.icon} label={info.label} value={file.name} />
                  <WhiteSpace height="16px" />
                </React.Fragment>
              )
            })}
            <WhiteSpace height="16px" />
            {addedFilesList.length > 0 && <Button onclick={handleUploadFiles} label={isUploading ? <LoaderDots /> : t('chargerGroups_upload')} width='100%' />}
          </BigModalContainer>
        </Modal>
      )}
      <ReleaseNotesContainer>
        <div style={{ display: 'flex' }}>
          <Icon path="settings.calibrateEvse" width="24px" height="24px" color="#4EFDA9" />
          <WhiteSpace width="8px" />
          <p style={{ fontSize: '14px', fontWeight: '700', color: '#ffffff', margin: '0px', marginTop: '3px' }}>{t('chargerGroups_releaseNotes')}</p>
        </div>
        {[{ link: 'https://voltie.atlassian.net/wiki/spaces/VOLTIE/pages/78184482/SW+Release+Notes', icon: 'chargerGroups.SW', label: 'SW' },
        { link: 'https://voltie.atlassian.net/wiki/spaces/VOLTIE/pages/345178113/FW+Release+Notes', icon: 'chargerGroups.FW', label: 'FW' },
        { link: 'https://voltie.atlassian.net/wiki/spaces/VOLTIE/pages/260931589/OS+Release+Notes', icon: 'chargerGroups.OS', label: 'OS' }].map(item => <React.Fragment key={item.label}>
          <WhiteSpace width="80px" />
          <Navigator to={item.link} newTab>
            <div style={{ display: 'flex', cursor: 'pointer' }}>
              <Icon path={item.icon} width="24px" height="24px" color="#4EFDA9" />
              <WhiteSpace width="8px" />
              <p style={{ fontSize: '14px', fontWeight: '700', color: '#ffffff', margin: '0px', marginTop: '3px' }}>{item.label}</p>
              <WhiteSpace width="4px" />
              <Icon path="general.forwardArrow" width="16px" height="16px" margintop="4px" color="#ffffff" />
            </div>
          </Navigator>
        </React.Fragment>)}
      </ReleaseNotesContainer>
      <PageTitle label={t('chargerGroups_updateChargers')} />
      <ToolbarButtonContainer>
        <div>
          <ButtonWithIcon icon="general.refresh" label={t('chargers_refresh')} onClick={handleRefresh} backgroundColor="success" iconColor="white" />
          <RefreshTimerLabel>{lastRefresh}</RefreshTimerLabel>
        </div>
        <WhiteSpace width="16px" />
        <div>
          <ButtonWithIcon icon="general.upload" label={t('chargerGroups_uploadFile')} onClick={() => { setIsUploadingFiles(true); setAddedFilesList([]) }} />
        </div>
        <WhiteSpace width="16px" />
        <div>
          <ButtonWithIcon icon="general.refresh" label={t('chargerGroups_refreshCloudFiles')} onClick={fetchBucketFiles} backgroundColor="dark" iconColor="white" />
        </div>
        <WhiteSpace width="16px" />
        <div>
          <ButtonWithIcon icon="general.plus" iconColor="white" label={t('chargerGroups_newChargerGroup')} onClick={handleAddNewGroupModalOpen} backgroundColor="dark" />
        </div>
      </ToolbarButtonContainer>
      <div style={{ display: 'flex' }}>
        <WhiteSpace width="25px" />
        <div style={{ position: 'relative' }}>
          <InputWithIcon onInput={(input) => { clearTimeout(searchDebounceRef.current); searchDebounceRef.current = setTimeout(() => handleGlobalSearch(input), 150) }} width="max(13vw, 166.4px)" placeholder={t('chargerGroups_search')} icon="general.search" iconColor="gray_600" />
          <SearchHelpBadge onClick={() => setIsShowingSearchHelp(true)}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#7E8299' }}>?</span>
          </SearchHelpBadge>
        </div>
        <WhiteSpace width="48px" />
        <FilterDropdownContainer $isActive={filterDropdownValue != 'All'}>
          <Dropdown isDark handleChoose={(value) => setFilterDropdownValue(value)} title={{ label: filterDropdownValue, icon: "general.filter", color: 'white' }} options={[{ label: 'All', color: '#ffffff' }, { label: 'Online', color: '#ffffff' }, { label: 'Offline', color: '#ffffff' }]} />
        </FilterDropdownContainer>
      </div>
      <WhiteSpace height="16px" />
      {!chargerGroups && (
        <div style={{ display: 'flex' }}>
          <WhiteSpace width="25px" />
          <LoaderDots />
        </div>
      )}
      <GroupsContainer onClick={() => setEditingChargerSoftwareVerion(null)}>
        {chargerGroups && Object.keys(chargerGroups).sort((a, b) => (chargerGroups[a].frontendOrder || Infinity) - (chargerGroups[b].frontendOrder || Infinity)).map((group, groupIndex) => (
          (globalSearchResults && (Object.keys(globalSearchResults).length < 1 || Object.keys(globalSearchResults).includes(group))) &&
          <GroupContainer key={group}>
            <GroupHeaderTable onClick={() => handleOpenGroup(group, groupIndex)} ref={el => scrollToGroupRef.current[groupIndex] = el as HTMLElement}>
              <tbody>
                <GroupHeaderRow>
                  <GroupInfoContainer $width="20%">
                    <Icon path={(() => {
                      switch (group) {
                        case 'Installed_chargers':
                          return "chargerGroups.installed_chargersGroup"
                        case 'Betatester':
                          return "chargerGroups.betatesterGroup"
                        case 'Default':
                          return "chargerGroups.defaultGroup"
                        case 'Insider_simuhat':
                          return "chargerGroups.insider_simuhatGroup"
                        default:
                          return "chargerGroups.chargerGroup"
                      }
                    })()} width="30px" height="30px" margintop="10px" marginright="16px" />
                    <GroupNameLabel>{group}</GroupNameLabel>
                  </GroupInfoContainer>
                  <GroupInfoContainer $width="10%">
                    <div>
                      <GroupInfoLabel>{t('chargerGroups_charger')}</GroupInfoLabel>
                      <GroupInfoValue>
                        {chargerGroups[group].chargers ? (
                          <>
                            {Object.keys(chargerGroups[group].chargers).length}
                            <span style={{ color: '#57D9BA' }}>
                              {` (${Object.values(chargerGroups[group].chargers).filter(charger => charger.online === true).length})`}
                            </span>
                          </>
                        ) : '0'}
                      </GroupInfoValue>
                    </div>
                  </GroupInfoContainer>
                  <GroupInfoContainer $width="10%">
                    <div>
                      <GroupInfoLabel style={{ marginTop: '10px' }}>{t('chargerGroups_lastEdit')}</GroupInfoLabel>
                      <GroupInfoValue>{chargerGroups[group].lastEdit ? new Date(chargerGroups[group].lastEdit).toString().split(' ').slice(1, 4).join(' ') : '-'}</GroupInfoValue>
                      <GroupInfoLabel>{chargerGroups[group].lastEdit ? new Date(chargerGroups[group].lastEdit).toString().split(' ').slice(4, 6)[0] : ''}</GroupInfoLabel>
                    </div>
                  </GroupInfoContainer>
                  <GroupInfoContainer $width="20%">
                    <Icon path="chargerGroups.SW" width="30px" height="30px" margintop="10px" marginright="16px" />
                    <div>
                      <GroupInfoLabel>{t('chargerGroups_defaultSoftware')}</GroupInfoLabel>
                      <GroupInfoValue>{stripPlatform(chargerGroups[group].groupSwVersion)}</GroupInfoValue>
                    </div>
                  </GroupInfoContainer>
                  <GroupInfoContainer $width="20%">
                    <Icon path="chargerGroups.FW" width="30px" height="30px" margintop="10px" marginright="16px" />
                    <div>
                      <GroupInfoLabel>{t('chargerGroups_firmwareSystem')}</GroupInfoLabel>
                      <GroupInfoValue>{chargerGroups[group].groupFwVersion}</GroupInfoValue>
                    </div>
                  </GroupInfoContainer>
                  <GroupInfoContainer $width="20%">
                    <Icon path="chargerGroups.OS" width="30px" height="30px" margintop="10px" marginright="16px" />
                    <div>
                      <GroupInfoLabel>{t('chargerGroups_operatingSystem')}</GroupInfoLabel>
                      <GroupInfoValue>{stripPlatform(chargerGroups[group].groupOsVersion)}</GroupInfoValue>
                    </div>
                  </GroupInfoContainer>
                </GroupHeaderRow>
              </tbody>
            </GroupHeaderTable>
            <EditButtonContainer onClick={(e) => { e.stopPropagation(); handleEditGroupModalOpen(group) }}>
              <Icon path="general.wrench" width="16px" height="16px" />
            </EditButtonContainer>
            <OpenButtonContainer $isOpen={group == openGroupName} onClick={(e) => { e.stopPropagation(); handleOpenGroup(group, groupIndex) }}>
              <Icon path="general.upArrow" width="100%" height="100%" />
            </OpenButtonContainer>
            {(group == openGroupName || Object.keys(globalSearchResults).length > 0) && (
              <>
                <WhiteSpace height="10px" />
                <GroupDividerLine />
                <WhiteSpace height="24px" />
                <GroupChargersScroller>
                <GroupChargersTable>
                  <tbody>
                    <ChargerInfoRow>
                      <td style={{ width: '30px' }}>{/* To make place for the charger icon */}</td>
                      <td style={{ width: '16%' }}>
                        <GroupChargersTableLabel>{t('chargerGroups_chargerIdUserEmailLocalName')}</GroupChargersTableLabel>
                      </td>
                      <td style={{ width: '20%' }}>
                        <GroupChargersTableLabel>{t('chargerGroups_state')}</GroupChargersTableLabel>
                      </td>
                      <td style={{ width: '20%' }}>
                        <GroupChargersTableLabel>{t('chargerGroups_defaultSoftware')}</GroupChargersTableLabel>
                      </td>
                      <td>
                        <GroupChargersTableLabel>{t('chargerGroups_firmwareSystem')}</GroupChargersTableLabel>
                      </td>
                      <td style={{ width: '19%' }}>
                        <GroupChargersTableLabel>{t('chargerGroups_operatingSystem')}</GroupChargersTableLabel>
                      </td>
                      <td style={{ width: '20px' }}>{/* To make place for the charger menu */}</td>
                    </ChargerInfoRow>
                    {chargerGroups[group].chargers && Object.keys(chargerGroups[group].chargers).map(charger => (
                      (Object.keys(globalSearchResults).length < 1 || globalSearchResults[group].includes(charger)) &&
                      (filterDropdownValue == 'All' ? true : filterDropdownValue == 'Online' ? chargerGroups[group].chargers[charger].online : !chargerGroups[group].chargers[charger].online) &&
                      <ChargerTableRow key={charger}>
                        <td>
                          <Icon path={chargerGroups[group].chargers[charger].online ? "chargerGroups.chargerOnline" : "chargerGroups.chargerOffline"} width="30px" height="30px" marginleft="9px" marginright="16px" />
                        </td>
                        <td style={{ paddingTop: '5px', paddingBottom: '5px' }}>
                          <div>
                            <Navigator to={`/chargers/${charger}`}><ChargerIdLabel>{charger}</ChargerIdLabel></Navigator>
                            <ChargerEmailLabel>{chargerGroups[group].chargers[charger].userEmail}</ChargerEmailLabel>
                            <ChargerLocalNameLabel>{chargerGroups[group].chargers[charger].localName != ' ' ? chargerGroups[group].chargers[charger].localName : '-'}</ChargerLocalNameLabel>
                          </div>
                        </td>
                        <td>
                          {chargerGroups[group].chargers[charger].online ? (
                            <ChargerStatus $status='online'>Online</ChargerStatus>
                          ) : (
                            <div>
                              <ChargerStatus $status='offline'>Offline</ChargerStatus>
                              <TableTimeAgoLabel>{dateToTimeAgoString(new Date(chargerGroups[group].chargers[charger].lastPresence.replace(/\./g, '-')))}</TableTimeAgoLabel>
                            </div>
                          )}
                        </td>
                        <td style={{ position: 'relative' }}>
                          <div>
                            <ChargerSoftwareVerisonLabel $isMismatch={isVersionMismatch(chargerGroups[group].groupSwVersion, chargerGroups[group].chargers[charger].swVersion, chargerGroups[group].chargers[charger].swVersionNumber, 'SW')}>{chargerGroups[group].chargers[charger].swVersionNumber || '-'}</ChargerSoftwareVerisonLabel>
                            <WhiteSpace height="22px" />
                            {(editingChargerSoftwareVerion && editingChargerSoftwareVerion.chargerId == charger && editingChargerSoftwareVerion.software == 'SW') ? (
                              <>
                                <WhiteSpace height="4px" />
                                <Select handleSelect={(value) => handleEditChargerSoftwareVersion(charger, group, value, 'swVersion')} width="max(12vw, 153.6px)" isAbsolute fitOptionsToContent options={[stripPlatform(chargerGroups[group].chargers[charger].swVersion), ...deduplicateFiles(bucketFiles.swVersion.filter(s => checkIfFilePassesGroupFilter(s, chargerGroups[group].frontendFilter?.SW || ""))), 'Delete']} />
                              </>
                            ) : (
                              <div style={{ display: 'flex', position: 'relative' }}>
                                {chargerGroups[group].chargers[charger].swVersion ? (
                                  <>
                                    <GroupInfoValue style={{ margin: '0px', marginTop: '4px' }}>{stripPlatform(chargerGroups[group].chargers[charger].swVersion)}</GroupInfoValue>
                                    <WhiteSpace width="8px" />
                                    <ChargerSoftwareIconContainer title="Edit" onClick={(e) => { e.stopPropagation(); setEditingChargerSoftwareVerion({ chargerId: charger, software: 'SW' }) }}>
                                      <Icon path="general.edit" color="gray_900" width="100%" height="100%" />
                                    </ChargerSoftwareIconContainer>
                                    <WhiteSpace width="8px" />
                                    <ChargerSoftwareIconContainer title="Delete" onClick={() => handleEditChargerSoftwareVersion(charger, group, 'Delete', 'swVersion')}>
                                      <Icon path="general.trash" color="danger" width="125%" height="125%" margintop="2px" />
                                    </ChargerSoftwareIconContainer>
                                    <WhiteSpace width="8px" />
                                    <ChargerSoftwareIconContainer title="Reactivate" onClick={(e) => { e.stopPropagation(); handleResetCharger(charger, group, false, true, false) }}>
                                      <Icon path="general.reset" color="gray_900" width="125%" height="125%" />
                                    </ChargerSoftwareIconContainer>
                                  </>
                                ) : (
                                  <>
                                    <ChargerSoftwareIconContainer title="Edit" onClick={(e) => { e.stopPropagation(); setEditingChargerSoftwareVerion({ chargerId: charger, software: 'SW' }) }}>
                                      <Icon path="general.edit" color="gray_900" width="100%" height="100%" />
                                    </ChargerSoftwareIconContainer>
                                    <WhiteSpace width="8px" />
                                    <ChargerSoftwareIconContainer title="Reactivate" onClick={(e) => { e.stopPropagation(); handleResetCharger(charger, group, false, true, false) }}>
                                      <Icon path="general.reset" color="gray_900" width="125%" height="125%" />
                                    </ChargerSoftwareIconContainer>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ position: 'relative' }}>
                          <div>
                            <ChargerSoftwareVerisonLabel $isMismatch={isVersionMismatch(chargerGroups[group].groupFwVersion, chargerGroups[group].chargers[charger].fwVersion, chargerGroups[group].chargers[charger].fwVersionNumber, 'FW')}>{chargerGroups[group].chargers[charger].fwVersionNumber || '-'}</ChargerSoftwareVerisonLabel>
                            <WhiteSpace height="22px" />
                            {(editingChargerSoftwareVerion && editingChargerSoftwareVerion.chargerId == charger && editingChargerSoftwareVerion.software == 'FW') ? (
                              <>
                                <WhiteSpace height="4px" />
                                <Select handleSelect={(value) => handleEditChargerSoftwareVersion(charger, group, value, 'fwVersion')} width="max(12vw, 153.6px)" isAbsolute fitOptionsToContent options={[chargerGroups[group].chargers[charger].fwVersion, ...bucketFiles.fwVersion.filter(s => checkIfFilePassesGroupFilter(s, chargerGroups[group].frontendFilter?.FW || "")), 'Delete']} />
                              </>
                            ) : (
                              <div style={{ display: 'flex', position: 'relative' }}>
                                {chargerGroups[group].chargers[charger].fwVersion ? (
                                  <>
                                    <GroupInfoValue style={{ margin: '0px', marginTop: '4px' }}>{chargerGroups[group].chargers[charger].fwVersion}</GroupInfoValue>
                                    <WhiteSpace width="8px" />
                                    <ChargerSoftwareIconContainer title="Edit" onClick={(e) => { e.stopPropagation(); setEditingChargerSoftwareVerion({ chargerId: charger, software: 'FW' }) }}>
                                      <Icon path="general.edit" color="gray_900" width="100%" height="100%" />
                                    </ChargerSoftwareIconContainer>
                                    <WhiteSpace width="8px" />
                                    <ChargerSoftwareIconContainer title="Delete" onClick={() => handleEditChargerSoftwareVersion(charger, group, 'Delete', 'fwVersion')}>
                                      <Icon path="general.trash" color="danger" width="125%" height="125%" margintop="2px" />
                                    </ChargerSoftwareIconContainer>
                                    <WhiteSpace width="8px" />
                                    <ChargerSoftwareIconContainer title="Reactivate" onClick={(e) => { e.stopPropagation(); handleResetCharger(charger, group, true, false, false) }}>
                                      <Icon path="general.reset" color="gray_900" width="125%" height="125%" />
                                    </ChargerSoftwareIconContainer>
                                  </>
                                ) : (
                                  <>
                                    <ChargerSoftwareIconContainer title="Edit" onClick={(e) => { e.stopPropagation(); setEditingChargerSoftwareVerion({ chargerId: charger, software: 'FW' }) }}>
                                      <Icon path="general.edit" color="gray_900" width="100%" height="100%" />
                                    </ChargerSoftwareIconContainer>
                                    <WhiteSpace width="8px" />
                                    <ChargerSoftwareIconContainer title="Reactivate" onClick={(e) => { e.stopPropagation(); handleResetCharger(charger, group, true, false, false) }}>
                                      <Icon path="general.reset" color="gray_900" width="125%" height="125%" />
                                    </ChargerSoftwareIconContainer>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ position: 'relative' }}>
                          <div>
                            <ChargerSoftwareVerisonLabel $isMismatch={isVersionMismatch(chargerGroups[group].groupOsVersion, chargerGroups[group].chargers[charger].osVersion, chargerGroups[group].chargers[charger].osVersionNumber, 'OS')}>{chargerGroups[group].chargers[charger].osVersionNumber || '-'}</ChargerSoftwareVerisonLabel>
                            <WhiteSpace height="22px" />
                            {(editingChargerSoftwareVerion && editingChargerSoftwareVerion.chargerId == charger && editingChargerSoftwareVerion.software == 'OS') ? (
                              <>
                                <WhiteSpace height="4px" />
                                <Select handleSelect={(value) => handleEditChargerSoftwareVersion(charger, group, value, 'osVersion')} width="max(12vw, 153.6px)" isAbsolute fitOptionsToContent options={[stripPlatform(chargerGroups[group].chargers[charger].osVersion), ...deduplicateFiles(bucketFiles.osVersion.filter(s => checkIfFilePassesGroupFilter(s, chargerGroups[group].frontendFilter?.OS || ""))), 'Delete']} />
                              </>
                            ) : (
                              <div style={{ display: 'flex', position: 'relative' }}>
                                {chargerGroups[group].chargers[charger].osVersion ? (
                                  <>
                                    <GroupInfoValue style={{ margin: '0px', marginTop: '4px' }}>{stripPlatform(chargerGroups[group].chargers[charger].osVersion)}</GroupInfoValue>
                                    <WhiteSpace width="8px" />
                                    <ChargerSoftwareIconContainer title="Edit" onClick={(e) => { e.stopPropagation(); setEditingChargerSoftwareVerion({ chargerId: charger, software: 'OS' }) }}>
                                      <Icon path="general.edit" color="gray_900" width="100%" height="100%" />
                                    </ChargerSoftwareIconContainer>
                                    <WhiteSpace width="8px" />
                                    <ChargerSoftwareIconContainer title="Delete" onClick={() => handleEditChargerSoftwareVersion(charger, group, 'Delete', 'osVersion')}>
                                      <Icon path="general.trash" color="danger" width="125%" height="125%" margintop="2px" />
                                    </ChargerSoftwareIconContainer>
                                    <WhiteSpace width="8px" />
                                    <ChargerSoftwareIconContainer title="Reactivate" onClick={(e) => { e.stopPropagation(); handleResetCharger(charger, group, false, false, true) }}>
                                      <Icon path="general.reset" color="gray_900" width="125%" height="125%" />
                                    </ChargerSoftwareIconContainer>
                                  </>
                                ) : (
                                  <>
                                    <ChargerSoftwareIconContainer title="Edit" onClick={(e) => { e.stopPropagation(); setEditingChargerSoftwareVerion({ chargerId: charger, software: 'OS' }) }}>
                                      <Icon path="general.edit" color="gray_900" width="100%" height="100%" />
                                    </ChargerSoftwareIconContainer>
                                    <WhiteSpace width="8px" />
                                    <ChargerSoftwareIconContainer title="Reactivate" onClick={(e) => { e.stopPropagation(); handleResetCharger(charger, group, false, false, true) }}>
                                      <Icon path="general.reset" color="gray_900" width="125%" height="125%" />
                                    </ChargerSoftwareIconContainer>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <Dropdown width={'175px'} handleChoose={(value) => handleChargerMenuSelect(value, charger, group)} title={{ icon: "general.more", label: '' }} options={[{ icon: "chargerGroups.datasheet", label: t('chargerGroups_datasheet'), link: `/chargers/${charger}` }, { icon: "general.log", label: t('chargerGroups_logs'), link: `/logs/charger/${charger}` }, { icon: 'general.refresh', label: t('chargerGroups_forceUpdateCheck') }, { icon: "chargerGroups.move", label: t('chargerGroups_move') }, { icon: "general.error", label: `${t('chargerGroups_reactivate')} (${t('general_all')})`, color: '#FD4E4E' }]} showOptionContainerOnLeft />
                        </td>
                      </ChargerTableRow>
                    ))}
                  </tbody>
                </GroupChargersTable>
                </GroupChargersScroller>
              </>
            )}
          </GroupContainer>
        ))}
      </GroupsContainer>
      {(editingGroup || isAddingGroup) && chargerGroups && (
        <Modal align="right" onEscape={() => { setEditingGroup(null); setIsAddingGroup(false) }}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => { setEditingGroup(null); setIsAddingGroup(false) }} />
            </ModalCloseButtonContainer>
            {isAddingGroup ? (
              <ModalTitle>{t('chargerGroups_newChargerGroup')}</ModalTitle>
            ) : (
              <ModalTitle>{editingGroup}</ModalTitle>
            )}
            <WhiteSpace height="34px" />
            {isAddingGroup && (
              <>
                <Input onInput={(input) => setEditChargerGroup_NameInput(input)} width="max(18vw, 230.4px)" placeholder={t('chargerGroups_newChargerGroupName')} />
                <WhiteSpace height="24px" />
              </>
            )}
            <div style={{ display: 'flex' }}>
              <ModalCategoryTitle style={{ width: '50%' }}>{t('chargerGroups_uniformSetup')}</ModalCategoryTitle>
              <WhiteSpace width="100%" />
              {!isAddingGroup && (
                <Icon path="general.edit" width="14px" height="14px" color="gray_800" margintop="4px" onClick={() => { setIsShowingEditGroupFilterModal(true) }} />
              )}
            </div>
            <WhiteSpace height="16px" />
            <Select handleSelect={(value) => handleGroupSwVersionSelect(value)} width="max(18vw, 230.4px)" icon="chargerGroups.SW" label={t('chargerGroups_defaultSoftware')} options={editingGroup ? [stripPlatform(chargerGroups[editingGroup].groupSwVersion), ...deduplicateFiles(bucketFiles.swVersion.filter(s => checkIfFilePassesGroupFilter(s, chargerGroups[editingGroup].frontendFilter?.SW || ""))), '-'] : ['-', ...deduplicateFiles(bucketFiles.swVersion)]} />
            <WhiteSpace height="16px" />
            <Select handleSelect={(value) => setFWVersionSelectorValue(value)} width="max(18vw, 230.4px)" icon="chargerGroups.FW" label={t('chargerGroups_firmwareSystem')} options={editingGroup ? [chargerGroups[editingGroup].groupFwVersion, ...bucketFiles.fwVersion.filter(s => checkIfFilePassesGroupFilter(s, chargerGroups[editingGroup].frontendFilter?.FW || "")), '-'] : ['-', ...bucketFiles.fwVersion]} />
            <WhiteSpace height="16px" />
            <Select handleSelect={(value) => handleGroupOsVersionSelect(value)} width="max(18vw, 230.4px)" icon="chargerGroups.OS" label={t('chargerGroups_operatingSystem')} options={editingGroup ? [stripPlatform(chargerGroups[editingGroup].groupOsVersion), ...deduplicateFiles(bucketFiles.osVersion.filter(s => checkIfFilePassesGroupFilter(s, chargerGroups[editingGroup].frontendFilter?.OS || ""))), '-'] : ['-', ...deduplicateFiles(bucketFiles.osVersion)]} />
            <WhiteSpace height="24px" />
            <ModalCategoryDivider />
            <WhiteSpace height="24px" />
            <ModalCategoryTitle>{t('chargerGroups_addChargers')}</ModalCategoryTitle>
            <WhiteSpace height="16px" />
            <InputWithIcon onInput={(input) => handleEditChargerGroup_Search(input)} width="max(18vw, 230.4px)" placeholder={t('chargerGroups_search')} icon="general.search" iconColor="gray_600" />
            {editChargerGroup_AddedChargers && editChargerGroup_AddedChargers.map(charger => (
              editChargerGroup_SearchResults.includes(charger) && (
                <ModalChargerContainer key={charger} onClick={() => handleEditChargerGroup_RemoveCharger(charger)}>
                  <Icon path="general.checkboxFull" width="16px" height="16px" margintop="12px" marginleft="12px" />
                  <div>
                    <ModalChargerLabel>{t('chargerGroups_chargerId')}</ModalChargerLabel>
                    <ModalChargerValue>{charger}</ModalChargerValue>
                  </div>
                </ModalChargerContainer>
              )
            ))}
            {editChargerGroup_SearchResults && editChargerGroup_SearchResults.map(charger => (
              !editChargerGroup_AddedChargers.includes(charger) && (
                <ModalChargerContainer key={charger} onClick={() => handleEditChargerGroup_AddCharger(charger)}>
                  <Icon path="general.checkboxEmpty" width="16px" height="16px" margintop="12px" marginleft="12px" />
                  <div>
                    <ModalChargerLabel>{t('chargerGroups_chargerId')}</ModalChargerLabel>
                    <ModalChargerValue>{charger}</ModalChargerValue>
                  </div>
                </ModalChargerContainer>
              )
            ))}
            <ModalSaveButtonContainer>
              <ModalSaveButton onClick={isAddingGroup ? handleAddNewChargerGroup : handleSaveEditedChargerGroup}>{editChargerGroup_isLoading ? <LoaderDots /> : isAddingGroup ? t('chargerGroups_add') : t('chargerGroups_save')}</ModalSaveButton>
              <ModalCancelButton onClick={() => { setEditingGroup(null); setIsAddingGroup(false) }}>{t('chargerGroups_cancel')}</ModalCancelButton>
            </ModalSaveButtonContainer>
          </ModalContainer>
        </Modal>
      )}
    </>
  )
}